/**
 * Week navigation + week-level actions: prev / this / next (URL-backed via
 * `?week=`), the KW label, the Outlook state, "Copy last week" (confirmed
 * when the target week already holds blocks) and the 00–24 toggle.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";

import type { OutlookState, WeekWindow } from "../../api/focus";
import { formatDateRange, isoWeek } from "../../lib/focusTime";
import { Modal } from "../Modal";
import { Pill, type PillTone } from "../Pill";

const OUTLOOK: Record<OutlookState, { label: string; tone: PillTone; hint: string }> = {
  synced: { label: "outlook synced", tone: "ok", hint: "Meetings and blocks match Outlook" },
  stale: { label: "outlook stale", tone: "signal", hint: "Showing the last known meetings" },
  not_connected: { label: "no outlook", tone: "muted", hint: "No mailbox connected – blocks stay local" },
  error: { label: "outlook error", tone: "alert", hint: "Outlook could not be read" },
  not_loaded: { label: "outlook –", tone: "muted", hint: "Meetings not loaded" },
};

export function WeekToolbar({
  week,
  isCurrent,
  hrefFor,
  outlookState,
  outlookMailbox,
  plannedBlocks,
  copying,
  refreshing,
  fullDay,
  onCopyLastWeek,
  onRefresh,
  onToggleFullDay,
}: {
  week: WeekWindow;
  isCurrent: boolean;
  /** Link target for another week (null = this week). */
  hrefFor: (start: string | null) => string;
  outlookState: OutlookState;
  outlookMailbox: string | null;
  /** Planned blocks already in this week — a copy asks first when > 0. */
  plannedBlocks: number;
  copying: boolean;
  refreshing: boolean;
  fullDay: boolean;
  onCopyLastWeek: () => void;
  onRefresh: () => void;
  onToggleFullDay: () => void;
}) {
  const [confirmCopy, setConfirmCopy] = useState(false);
  const kw = isoWeek(week.start_date);
  const outlook = OUTLOOK[outlookState] ?? OUTLOOK.not_loaded;

  return (
    <div className="week-toolbar">
      <div className="flex items-center gap-1">
        <Link className="btn btn-sm btn-ghost" to={hrefFor(week.prev_start)} aria-label="Previous week">
          ←
        </Link>
        <Link
          className={clsx("btn btn-sm", isCurrent ? "btn-second" : "btn-ghost")}
          to={hrefFor(null)}
        >
          This week
        </Link>
        <Link className="btn btn-sm btn-ghost" to={hrefFor(week.next_start)} aria-label="Next week">
          →
        </Link>
      </div>
      <div className="week-toolbar-label">
        <span className="font-semibold text-ink">KW {kw.week}</span>
        <span>{formatDateRange(week.start_date, week.end_date)}</span>
      </div>
      <div className="flex items-center gap-2 ml-auto flex-wrap">
        <span title={outlookMailbox ? `${outlook.hint} · ${outlookMailbox}` : outlook.hint}>
          <Pill value={outlook.label} tone={outlook.tone} />
        </span>
        <button type="button" className="btn btn-sm btn-ghost" disabled={refreshing} onClick={onRefresh}>
          {refreshing ? "Syncing…" : "Sync"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={copying}
          onClick={() => (plannedBlocks > 0 ? setConfirmCopy(true) : onCopyLastWeek())}
        >
          {copying ? "Copying…" : "Copy last week"}
        </button>
        <button
          type="button"
          className={clsx("pref-toggle", fullDay && "is-on")}
          aria-pressed={fullDay}
          onClick={onToggleFullDay}
        >
          <span className="pref-toggle-box" aria-hidden>
            {fullDay ? "[x]" : "[ ]"}
          </span>
          00–24
        </button>
      </div>

      <Modal
        open={confirmCopy}
        onClose={() => setConfirmCopy(false)}
        eyebrow={`KW ${kw.week}`}
        title="Copy last week into this one?"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmCopy(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-main"
              onClick={() => {
                setConfirmCopy(false);
                onCopyLastWeek();
              }}
            >
              Copy
            </button>
          </>
        }
      >
        <p className="text-sm text-ink-medium">
          This week already has {plannedBlocks} planned {plannedBlocks === 1 ? "block" : "blocks"}. Last
          week&rsquo;s blocks are added at the same weekday and time; any that overlap a block, a busy
          meeting or lie in the past are skipped.
        </p>
      </Modal>
    </div>
  );
}
